import React, { useEffect, useState, useCallback, useContext } from 'react';
import { Container, Row, Col, Modal, ModalBody } from "reactstrap";
import PDFFlipbook from '../PDFFlipbook/PDFFlipbook';
import Carousel from '../Common/Carousel/Carousel';
import { getProductRange } from '../../api/productrangeService';
import { generateRedirectLogin } from '../../api/loginService';
import './ProductCarousel.css';
import AuthContext from "../../Context/AuthContext";
import PageBanner from '../Common/PageBanner';
import { useNavigate } from "react-router-dom";
import FullScreenLoader from "../../components/Common/Loader";
import CommonToastContainer from "../../components/Common/CommonToastContainer";
import config from "../../config";

const ProductCarousel = () => {

    const { checkIsUserLoggedIn } = useContext(AuthContext);
    const isLoggedIn = checkIsUserLoggedIn();
    const [showLoader, setShowLoader] = useState(false);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState('');
    const [selectedCatalog, setSelectedCatalog] = useState('');
    const [catalogSize, setCatalogSize] = useState('');
    const [pageNo, setPageNo] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const navigate = useNavigate();
    const [isViewerOpen, setIsViewerOpen] = useState(false);
    const [pdfFailed, setPdfFailed] = useState(false);
    const [viewerSrc, setViewerSrc] = useState("");
    const [viewerTitle, setViewerTitle] = useState("");
    const [progress, setProgress] = useState(0);
    const [dataReady, setDataReady] = useState(false);
    const [toasts, setToasts] = useState([]);
    const [paginationBlocked, setPaginationBlocked] = useState(false);

    const hasValidImages = (items = []) => {
        return items.some(
            item => item.Image && item.Image.trim() !== ''
        );
    };

    const [failedCategories, setFailedCategories] = useState(new Set());

    const handleCarouselError = (categoryId) => {
        setFailedCategories(prev => new Set(prev).add(categoryId));
    };

    const showToast = (toastData) => {
        setToasts((prev) => [...prev, { id: Date.now(), ...toastData }]);
    };

    const removeToast = (id) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    };

    const fetchCategories = useCallback(async (page = 1, isLoadMore = false) => {
        if (paginationBlocked) return;

        setShowLoader(true);
        setDataReady(false);

        try {
            if (isLoadMore) {
                setLoadingMore(true);
            } else {
                setLoading(true);
            }

            const params = {
                pageNo: page,
                pageSize: 6
            };

            const data = await getProductRange(params);
            if (data?.ZMESSAGE?.[0]?.RETURNCODE === "0") {
                console.error("API Error:", data?.ZMESSAGE?.[0]?.MESSAGE);

                setError(data?.ZMESSAGE?.[0]?.MESSAGE || "Something went wrong");
                setHasMore(false);
                setPaginationBlocked(true); 
                return;
            }

            if (Array.isArray(data.Data) && data.Data.length > 0) {
                setCategories(prev =>
                    isLoadMore ? [...prev, ...data.Data] : data.Data
                );

                if (data.Data.length < params.pageSize) {
                    setHasMore(false);
                }
            } else {
                setHasMore(false);
            }

        } catch (err) {
            console.error("Network Error:", err);

            setError("Network error. Please try again later.");
            setHasMore(false);
            setPaginationBlocked(true); 
        } finally {
            setProgress(100);
            setLoading(false);
            setLoadingMore(false);

            setTimeout(() => {
                setShowLoader(false);
                setDataReady(true);
                setProgress(0);
            }, 400);
        }
    }, [paginationBlocked]);

    useEffect(() => {
        fetchCategories(1, false);
    }, [fetchCategories]);

    useEffect(() => {
        if (!showLoader) return;
        let value = 10;
        setProgress(value);
        const interval = setInterval(() => {
            value = Math.min(value + Math.random() * 15, 90);
            setProgress(value);
        }, 400);
        return () => clearInterval(interval);
    }, [showLoader]);

    const loadMore = useCallback(() => {
        if (
            loadingMore ||
            !hasMore ||
            paginationBlocked || 
            loading
        ) {
            return;
        }

        const nextPage = pageNo + 1;
        setPageNo(nextPage);
        fetchCategories(nextPage, true);
    }, [loadingMore, hasMore, paginationBlocked, loading, pageNo, fetchCategories]);

    useEffect(() => {
        const handleScroll = () => {
            if (window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - 100) {
                loadMore();
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [loadMore]);

    const handleCatalogClick = async (category) => {
        if (!isLoggedIn) {
            showToast({
                title: "Warning",
                message: "Please login to view or download our catalog.",
                type: "warning",
                autoClose: 1000,
            });
            return;
        }
        if (!category.Pdf) {
            showToast({
                title: "Warning",
                message: "PDF file not available.",
                type: "warning",
            });
            return;
        }
        setViewerSrc(category.Pdf);
        setViewerTitle(category.Title || "Catalog");
        setIsViewerOpen(true);
        console.log("PDF value:", JSON.stringify(category.Pdf));
    };

    const handleSignUp = async () => {
        try {
            const res = await generateRedirectLogin();
            window.open(res.redirectUrl, "_blank", "noopener,noreferrer");
        } catch {
            window.open(config.globalSettings.signupRedirectUrl, "_blank", "noopener,noreferrer");
        }
    };

    return (
        <>
            <CommonToastContainer toasts={toasts} removeToast={removeToast} />
            {showLoader && <FullScreenLoader progress={progress} />}
            <div className="product-range-section">
                <PageBanner backgroundImage="/assets/images/banner/image4.png" height="70vh" overlay={false}/>
                <section className="product-carousel-section">
                    <Container className="d-flex flex-column gap-5" data-aos="fade-in">
                        <Row>
                            <Col>
                                <div className="section-header text-center">
                                    <h1 className="section-product-title text-primary">
                                        Discover Our Product Range Offerings
                                    </h1>
                                    <h5 className="section-product-subtitle text-primary">
                                        Designed to please. Engineered to Sell.
                                    </h5>
                                    <p className="section-description text-primary m-0">
                                        All our jewellery is available in 9KT, 14KT, 18KT and Vermeil.
                                    </p>
                                </div>
                            </Col>
                        </Row>

                        {loading ? (
                            <div className="loading-state text-center text-primary">
                                <div className="loading-spinner"></div>
                                <p>Loading categories...</p>
                            </div>
                        ) : error ? (
                            <div className="error-state text-center">
                                <p>{error}</p>
                                <button className="retry-button" onClick={() => fetchCategories(1, false)}>
                                    Retry
                                </button>
                            </div>
                        ) : (
                            categories.map((category, index) => {
                                const categoryKey = category.Id || index;
                                const hasImages = hasValidImages(category.Items);
                                const hasFailed = failedCategories.has(categoryKey);
                                if (!hasImages || hasFailed) return null;
                                return (
                                    <div key={categoryKey} className="category-section">
                                        <Row className="category-carousel">
                                            {hasValidImages(category.Items) && (
                                                <Col lg={8} md={12} className="mb-lg-0">
                                                    <Carousel
                                                        items={category.Items || []}
                                                        onError={() => handleCarouselError(categoryKey)}
                                                    />
                                                </Col>
                                            )}
                                            <Col
                                                lg={hasValidImages(category.Items) ? 4 : 12}
                                                md={12}
                                                className="d-flex flex-column align-items-center"
                                                data-aos="fade-left"
                                            >
                                                <div className="category-info-carousel">
                                                    <h3 className="category-title text-center text-primary">
                                                        {category.Title}
                                                    </h3>
                                                </div>
                                                <div className="catalog-button-container">
                                                    <button className="catalog-button" onClick={() => handleCatalogClick(category)}>
                                                        Download Catalog
                                                    </button>
                                                </div>
                                            </Col>
                                        </Row>
                                    </div>
                                );
                            })
                        )}

                        {loadingMore && <div>Loading more...</div>}

                        <section className="product-range-register">
                            <Container>
                                <Row className="align-items-center">
                                    <Col md="6" className="text-center" data-aos="fade-right">
                                        <img
                                            src="/assets/images/product-range-signup.png"
                                            alt="Product Range Register"
                                            className="img-fluid product-range-image"
                                        />
                                    </Col>
                                    <Col md="6" className="text-center product-range-desc" data-aos="fade-left">
                                        <h2 className="fw-bold text-primary mt-2">
                                            Sign-up for unmatched <br />
                                            convenience at your fingertips
                                        </h2>
                                        <p className="text-primary product-range-description">
                                            With our customer portal, you can access a wide design bank
                                            of over 3000+ pcs, get quick access to our latest collections,
                                            make selections, and directly connect with a dedicated
                                            representative.
                                        </p>
                                        {/*<a href={config.globalSettings.signupRedirectUrl} className="product-range-btn" target="_blank" rel="noopener noreferrer">*/}
                                        {/*    Sign Up Now*/}
                                        {/*</a>*/}
                                        <button
                                            type="button"
                                            className="product-range-btn"
                                            onClick={handleSignUp}
                                        >
                                            Sign Up Now
                                        </button>
                                    </Col>
                                </Row>
                            </Container>
                        </section>
                    </Container>
                </section>

                <Modal
                    isOpen={isViewerOpen}
                    toggle={() => setIsViewerOpen(false)}
                    size="xl"
                    centered
                    modalClassName="no-bg-modal"
                    contentClassName="no-bg-content"
                >
                    <ModalBody className="p-0 position-relative">
                        <button className="viewer-close-icon" onClick={() => setIsViewerOpen(false)} aria-label="Close">
                            &times;
                        </button>

                        <iframe src={viewerSrc} title="PDF Viewer" style={{ width: "100%", height: "97vh", border: "none" }} onLoad={() => setPdfFailed(false)}
                            onError={() => {
                                setIsViewerOpen(false);
                                navigate("/vault-access");
                            }}
                        />
                    </ModalBody>
                </Modal>

                <Modal isOpen={isViewerOpen} toggle={() => setIsViewerOpen(false)} size="xl" centered>
                    <ModalBody className="p-0 position-relative">
                        <button className="viewer-close-icon" onClick={() => setIsViewerOpen(false)}>
                            &times;
                        </button>
                        <iframe
                            src={viewerSrc}
                            title={viewerTitle}
                            style={{ width: "100%", height: "97vh", border: "none" }}
                            onLoad={() => setPdfFailed(false)}
                            onError={() => {
                                setPdfFailed(true);
                                setIsViewerOpen(false);

                                showToast({
                                    title: "Error",
                                    message: "Unable to load the PDF. Please try again later.",
                                    type: "error",
                                    autoClose: 1500,
                                });
                            }}
                        />
                    </ModalBody>
                </Modal>
            </div>
        </>
    );
};

export default ProductCarousel;